/*!
// <copyright file="ListSyncManager.js" company="Microsoft">
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
*/

/// <reference path="../html-binding-controls.ts" />
/// <reference path="../html-binding-handlers.ts" />
/// <reference path="Grid.js" />

(function () {
    'use strict';

    var PendingRowStates = {
        pending: $dyn.debug.general ? 'pending' : 0,
        processing: $dyn.debug.general ? 'processing' : 1,
        processed: $dyn.debug.general ? 'processed' : 2,
        notQueued: $dyn.debug.general ? 'notQueued' : 3,
    };

    var PendingRowTypes = {
        newRow: $dyn.debug.general ? 'newRow' : 0,
        existingRow: $dyn.debug.general ? 'existingRow' : 1,
    };

   /**
    * @memberof $dyn.controls.List.SyncManager
    * @typedef PendingRowItem
    * @private
    *
    * @prop {Integer} id
    * @prop {Integer} rowId
    * @prop {Integer} type
    * @prop {Integer} status
    * @prop {Object} state
    * @prop {Boolean} isDirty
    * @prop {Object} executeActions
    * @prop {Boolean} processImmediate
    * @prop {Boolean} isFromKeyDown
    * @prop {Boolean} rowKeyDownNewRowInNonFastEditMode
    */

    /**
     * @class SyncManager
     * @classdesc The SyncManager component of the List control
     * @summary Responsible for synchronizing user grid interactions (i.e row change, cell edits, marking, default navigation etc.) with the server
     * @memberof $dyn.controls.List
     * @internal
     * @param {Object} listContext - Reference to List object that owns the SyncManager
     * @param {Object} dataProvider - Reference to the Data Provider
     */
    $dyn.controls.List.SyncManager = function (listContext, dataProvider) {
        var self = this;
        this._listContext = listContext;
        this._dataProvider = dataProvider;
        /**
        * @memberof $dyn.controls.List.SyncManager#
        * @member
        * @public
        */
        this.invalidRow = $dyn.observable(null);
        /**
        * @memberof $dyn.controls.List.SyncManager#
        * @member
        * @public
        */
        this._leaveRecordRowIndex = undefined;
        this._activeRowValid = $dyn.computed(function () {
            var invalidRow = $dyn.value(self.invalidRow);
            return !invalidRow;
        });
        this._disposables = [];
        this._shouldReprocess = $dyn.observable(false);
        this._syncInProgress = $dyn.observable(false);
        this._pendingRowChange = $dyn.observable(false);
        /**
        * @memberof $dyn.controls.List.SyncManager#
        * @member
        * @public
        */
        this._syncEngineState = $dyn.observable();

        /**
        * @memberof $dyn.controls.List.SyncManager#
        * @member
        * @public
        */
        this.isQueueEmpty = $dyn.computed(this._isQueueEmpty, undefined, this);
        this._pendingRowIdToPendingRow = {};
        /**
        * @memberof $dyn.controls.List.SyncManager#
        * @member
        * @public
        */
        this._pendingRowsQueue = $dyn.observableArray([]);
        this._pendingRowsToDirectInteractionQueue = $dyn.observableArray([]);
        this._disposables.push($dyn.observe(this._dataProvider.InternalSelectedRowId, this.internalSelectedRowUpdated, this, null, true));
        this._disposables.push($dyn.observe(this._pendingRowsToDirectInteractionQueue, this.pendingRowsToDirectInteractionQueueUpdated, this));
        this._disposables.push($dyn.observe(
            function () {
                return self._getSyncEngineState();
            },
            function (value) {
                self._syncEngineState(value);
            }));

        /**
        * @memberof $dyn.controls.List.SyncManager#
        * @member
        * @public
        */
        this.rowSyncState = $dyn.observable({});
        // The set of the edit changes that are currently in the queue
        this._pendingEdits = $dyn.observable({});
    };

    $dyn.controls.List.SyncManager.prototype = {
        rowSyncState: undefined,
        _dataProvider: null,
        _currentProcessingRow: undefined,
        //A flag that determines the validity of the active row.
        //A row is marked invalid if either the row change operations fails OR the cell synchronization fails
        _activeRowValid: undefined,
        //A flag that's set to true every time user selects the active row
        _shouldReprocess: undefined,
        //State of the engine that other components (like Renderer) can observe.
        //Currently, there are two states - Active and Inactive.
        _syncEngineState: undefined,
        //A flag that determines if sync between client grid and server are currently in progress
        _syncInProgress: undefined,
        _pendingEdits: undefined,
        _pendingRowIdToPendingRow: undefined,
        //Queue having rows with pending cell edits or execute actions
        _pendingRowsQueue: undefined,
        //Queue having rows that are sent directly to the Interaction Queue
        //These are only row change interactions (ChangeSelectedIndex command)
        //These row changes could get throttled at the Interaction Queue
        _pendingRowsToDirectInteractionQueue: undefined,
        _disposables: undefined,

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Integer} internalRowId - The internal row id of the row to process
        * @summary Handles changes to the internal row id
        */
        internalSelectedRowUpdated: function (internalRowId) {
            $dyn.log.list && $dyn.log.list("List: [SyncManager] Entering SyncManager::internalSelectedRowUpdated");
            if (internalRowId) {
                var isNewRowOnKeyDown = this.isNewRowOnKeyDown();

                if (this.inErrorState() || !this.isQueueEmpty() || this.isNewRow(internalRowId)) {
                    //Row change request needs to get into the grid queue
                    var inputMode = this._listContext.InputMode;

                    if (this._shouldQueuePendingRow(internalRowId, false)) {
                        //Create pending row item
                        var pendingRow = this.createPendingRow({
                            rowId: internalRowId,
                        });

                        this.setPendingRowType(pendingRow, isNewRowOnKeyDown);

                        pendingRow.processImmediate = pendingRow.processImmediate ||
                                                        inputMode === $dyn.ui.InputType.mouse ||
                                                        this._listContext._markingAction === $dyn.controls.List.MarkMode.toggle;

                        pendingRow.isDirty = pendingRow.isDirty || this._listContext._markingAction === $dyn.controls.List.MarkMode.toggle;

                        this.queuePendingRow(pendingRow);
                        var isFastEdit = $dyn.peek(this._listContext._fastEdit);
                        if(!isFastEdit || this.isQueueEmpty()){
                            this.processQueue();
                        }
                            //ClearMessages ensures we were on Active row and below check will make sure we are trying to go to a different row. Assumption is messages can only be associated to active row.
                        else if(!this.isQueueEmpty() && $dyn.peek(this._dataProvider.DataSelectedRowId) !== internalRowId) {
                            if(this._listContext && this._listContext.Root && this._listContext.Root.MessageBar && this._listContext.ClearMessages) {
                                // this is a temporary fix until Deliverable 1511094 and 331611 are complete
                                // The server will resend validation messages for data that wasn’t fixed so this is 
                                // safe until we complete Deliverable 1511094 and 331611
                                this._listContext.Root.MessageBar._clearWarningMessagesNotAttachedToInputControls();
                                this._listContext.ClearMessages = false;
                            }
                            this.processQueue();
                        }
                        else if (this._dataProvider.deleteTriggered && !this.isQueueEmpty()) {
                            this.processQueue();
                            this._dataProvider.deleteTriggered = false;
                            this._dataProvider._firstRowDeleted = false;
                        }
                    }
                }
                else {
                    //Row change request can be sent directly to the Interaction Queue
                    //where the interaction can be throttled
                    var rowIndex = this._listContext._renderer.getActivePageRelatedIndex(internalRowId);
                    //rowIndex can be undefined when the grid is still rendering for the first time
                    //In that case the change row is to the active row, and can be ignored
                    if (rowIndex !== undefined) {
                        if (this._shouldQueuePendingRow(internalRowId, true)) {
                            var pendingRow = { rowId: internalRowId };

                            this._pendingRowsToDirectInteractionQueue.push(pendingRow);
                            //this._setRowStateToProcessing(pendingRow);
                            var activeRowIndex = $dyn.value(this._dataProvider.SelectedIndex);
                            //We only want to send the leaveRecord one time, for each new active row
                            //So in scenarios where user keys up/down fast, we only send one
                            //change row interaction corresponding to the leave record, but send multiple
                            //actual change row interactions.
                            if (this._listContext._fastEditingAllowed() && this._leaveRecordRowIndex !== activeRowIndex) {
                                this._leaveRecordRowIndex = activeRowIndex;
                                $dyn.log.list && $dyn.log.list("List: [SyncManager] Queueing change to row index %s directly to interaction queue", activeRowIndex);
                                this._leaveRecord(false);
                            }
                            $dyn.log.list && $dyn.log.list("List: [SyncManager] Queueing change to row index %s directly to interaction queue", rowIndex);
                            this.changeRow(rowIndex, internalRowId);
                        }
                    }
                }
            }

            $dyn.log.list && $dyn.log.list("List: [SyncManager] Leaving SyncManager::internalSelectedRowUpdated");
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Object} pendingRowsToDirectInteractionQueue - The queue that keeps track of interactions going directly to the interaction queue
        * @summary Handles updates to the pendingRowsToDirectInteractionQueue
        */
        pendingRowsToDirectInteractionQueueUpdated: function (pendingRowsToDirectInteractionQueue) {
            if (this.isPendingRowsToDirectInteractionQueueEmpty()) {
                this.processQueue();
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @public
        * @summary Determines whether there are pending edits in the grid
        * @returns {Boolean} True if there are pending edits, false otherwise
        */
        hasPendingEdits: function () {
            return Object.keys($dyn.peek(this._pendingEdits)).length;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @public
        * @param {Integer} rowId - The row id to check for pending edits
        * @summary Determines whether there are pending edits in the specified row
        * @returns {Boolean} True if there are pending edits for this row, false otherwise
        */
        rowHasPendingEdit: function (rowId) {
            return !!$dyn.peek(this._pendingEdits)[rowId];
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @public
        * @summary Determines whether the pending rows queue is empty or not
        * @returns {Boolean} True if the pending rows queue is empty, false otherwise
        */
        _isQueueEmpty: function () {
            return !$dyn.value(this._pendingRowsQueue).length;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @summary Determines whether the pending rows to direct interaction queue is empty or not
        * @returns {Boolean} True if the pending rows to direct interaction queue is empty, false otherwise
        */
        isPendingRowsToDirectInteractionQueueEmpty: function () {
            return !$dyn.peek(this._pendingRowsToDirectInteractionQueue).length;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @public
        * @summary Determines whether the grid is in an error state, by checking if there's an invalid row
        * @returns {Boolean} True if there is an invalid row, false otherwise
        */
        inErrorState: function () {
            return !!$dyn.peek(this.invalidRow);
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Integer} internalRowId - The row id to check for active row
        * @summary Determines whether the specified row is the active row or not, by comparing with the DataSelectedRowId (i.e server active row)
        * @returns {Boolean} True if the user selected row id is same as the server selected row id, false otherwise
        */
        isActiveRow: function (internalRowId) {
            var dataSelectedRowId = this._dataProvider.DataSelectedRowId();
            $dyn.log.list && $dyn.log.list("List: [SyncManager] InternalSelectedRowId: %s, DataSelectedRowId: %s", internalRowId, dataSelectedRowId);
            return internalRowId === dataSelectedRowId;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @summary When a new row is created by a key down, a DataProvider provider is set to true. This method checks that property and resets it to false, if true
        * @returns {Boolean} True if a key down resulted in a new row, false otherwise
        */
        isNewRowOnKeyDown: function () {
            var newRowOnKeyDown = this._dataProvider.getIsCreateRowFromKeyDown();
            if (newRowOnKeyDown) {
                //We only need to access this property once from SyncManager, and after that we have to reset it on the data provider
                //Note: At some point we may have to look at a property bag that the data provider can send to the SyncManager whenever the internal
                // selected row gets updated
                this._dataProvider.setIsCreateRowFromKeyDown(false);
            }
            return newRowOnKeyDown;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @public
        * @param {Integer} rowId - row id of pending row to remove from queue
        * @summary Removes the pending row from the queue and clears out associated maps/lists/flags
        * @returns {Boolean} True if it successfully removed a pending row from queue, false otherwise
        */
        removeRowFromQueue: function (rowId) {
            for (var i = 0; i < $dyn.peek(this._pendingRowsQueue).length; ++i) {
                var pendingRow = $dyn.peek(this._pendingRowsQueue)[i];
                if (pendingRow && pendingRow.rowId === rowId) {
                    if (pendingRow.status === PendingRowStates.pending) {
                        $dyn.ui.updateBlock(null, function () {
                            this._pendingRowsQueue.splice(i, 1);
                            delete this._pendingRowIdToPendingRow[rowId];
                            this._removePendingEdit(rowId);
                        }, this);
                        return true;
                    } else {
                        return false;
                    }
                }
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @public
        * @summary Creates new row on key down in non-fast edit grid. This method is called directly by the RowOperationsManager without going through the  DataProvider to update the internal row
        */
        rowKeyDownNewRowInNonFastEditMode: function () {
            //Create pending row item
            var pendingRow = this.createPendingRow({
            });

            pendingRow.type = PendingRowTypes.newRow;
            pendingRow.processImmediate = true;
            //Row is not created until the call returns from server
            //By keeping this flag false we have the ability to delete the row item from the queue
            //in the event create row fails on server
            pendingRow.isDirty = false;
            pendingRow.isFromKeyDown = true;
            pendingRow.rowKeyDownNewRowInNonFastEditMode = true;

            this.queuePendingRow(pendingRow);
            this.processQueue();
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Integer} internalRowId - row id to check whether to queue or not
        * @param {Boolean} canThrottle - Flag to determine which queue to check (PendingRowsQueue or PendingRowsToDirectInteractionQueue)
        * @summary Determines whether the specified row needs to be queued into the pending rows queue or not
        * @returns {Boolean} True if this row id has to be queued, false otherwise
        */
        _shouldQueuePendingRow: function (internalRowId, canThrottle) {
            var shouldQueuePendingRow = true;
            if (this.isActiveRow(internalRowId)) {
                if (!this._shouldReprocess()) {
                    //User is on the active row
                    //Set flag to true so that any changes made by the user can be processed
                    $dyn.log.list && $dyn.log.list("List: [SyncManager] Should reprocess is true");
                    this.setShouldReprocess(true);
                }

                $dyn.log.list && $dyn.log.list("List: [SyncManager] User on active row");
                var queue = canThrottle ? this._pendingRowsToDirectInteractionQueue() : this._pendingRowsQueue();
                if (queue.length === 0) {
                    $dyn.log.list && $dyn.log.list("List: [SyncManager] Pending rows queue is empty. shouldQueuePendingRow: false ");
                    //If there are any other marked rows, those need to be unmarked even if we are on the active row
                    //We can make this happen by sending a change row interaction to the server and the server will unmark the other rows
                    shouldQueuePendingRow = false;//this._dataProvider.hasMarkedRows(true);
                }
                else {
                    var tail = queue[queue.length - 1];
                    if (tail.rowId === internalRowId) {
                        $dyn.log.list && $dyn.log.list("List: [SyncManager] Not changing rows. shouldQueuePendingRow: false");
                        shouldQueuePendingRow = false;
                    }
                    else {
                        $dyn.log.list && $dyn.log.list("List: [SyncManager] Changing rows. shouldQueuePendingRow: true");
                    }
                }
            }
            else {
                $dyn.log.list && $dyn.log.list("List: [SyncManager] Change requested to row that's not the current active row. shouldQueuePendingRow: true");
            }
            return shouldQueuePendingRow;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Integer} internalRowId - The internal row id
        * @summary First checks if there's a corresponding data row id (server row) for this internal row, if not, it checks if there's a pivoted row against which the new row is being created
        * @returns {Boolean} True if it is a new row, false otherwise
        */
        isNewRow: function (internalRowId) {
            // An internal row id is considered to be a new row if there is no appropriate data row id but given internal row id has relative data row id
            var dataRowId = this._dataProvider.getDataRowIdByInternalRowId(internalRowId);
            //dataRowId == -1 means the rows are existing but pending and we have no committed rows yet to pivot against.
            if (!dataRowId || dataRowId == -1) {
                var relativeDataRowId = this._dataProvider.getRelativeDataRowIdByInternalRowId(internalRowId);
                if (relativeDataRowId) {
                    return true;
                }
            }
            return false;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Object} pendingRow - The pending row item
        * @param {Boolean} isNewRowOnKeyDown - Flag that determines whether this row is a new row created via key down
        * @summary Sets the row type as newRow or existingRow based on the call to isNewRow API
        */
        setPendingRowType: function (pendingRow, isNewRowOnKeyDown) {
            var internalRowId = pendingRow.rowId;

            if (this.isNewRow(internalRowId)) {
                //New row
                pendingRow.type = PendingRowTypes.newRow;
                pendingRow.isFromKeyDown = isNewRowOnKeyDown;
                pendingRow.isDirty = true;
                pendingRow.processImmediate = true;
            }
            else {
                //Existing row
                pendingRow.type = PendingRowTypes.existingRow;
                pendingRow.isDirty = false;
                pendingRow.processImmediate = false;
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Integer} dataRowId - The data row id, i.e the master tracking id of the row set on the server
        * @summary Triggered when DataProvider updates the DataSelectedRowId upon change row or new row
        */
        dataSelectedRowUpdated: function (dataRowId) {
            $dyn.log.list && $dyn.log.list("List: [SyncManager] Entering SyncManager::dataSelectedRowUpdated");
            $dyn.log.list && $dyn.log.list("List: [SyncManager] DataSelectedRowId: %s", dataRowId);
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @public
        * @param {Integer} internalRowId - The internal row id selected by the user
        * @summary Triggered when DataProvider updates the DataSelectedRowId upon change row or new row. Enqueues a ToggleMark execute action on the pending row
        */
        queueToggleMark: function (internalRowId) {
            var executeAction = function () {
                $dyn.applyFunction(this.ToggleMark, this);
            };
            this._queueExecuteAction(internalRowId, 'ToggleMark', executeAction);
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @public
        * @param {Integer} internalRowId - The internal row id selected by the user
        * @summary Triggered when user presses Enter on a specific row to either select that record in a lookup OR navigate to that record in a hyperlink. Enqueues a DefaultAction execute action on the pending row
        */
        queueDefaultAction: function (internalRowId) {
            var executeAction = function () {
                $dyn.applyFunction(this.NavigationAction, this);
            };
            this._queueExecuteAction(internalRowId, 'DefaultAction', executeAction);
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @public
        * @param {Integer} internalRowId - The internal row id selected by the user
        * @param {Integer} delta - The number of rows to change to, starting from the internal row id
        * @param {Boolean} isNewRow - A flag that determines if the internal row is a new row
        * @summary Triggered when user does page up/ page down. Enqueues a change row execute action
        */
        queueChangeRowByDelta: function (internalRowId, delta, isNewRow) {
            var that = this;
            var listContext = this._listContext;
            var executeAction = function () {
                var currentIndex;
                var selectedIndex = $dyn.peek(that._dataProvider.SelectedIndex);
                if (internalRowId === undefined) {
                    currentIndex = selectedIndex;
                }
                else {
                    currentIndex = that._listContext._renderer.getActivePageRelatedIndex(internalRowId);
                    if (currentIndex === undefined) {
                        currentIndex = selectedIndex;
                    }
                }
                var targetIndex = delta + currentIndex;
                var targetCacheIndex = listContext.ConvertPageIndexToServerCacheIndex(targetIndex);

                if (!$dyn.value(listContext.InfiniteScroll)) {
                    var totalRowsCount = $dyn.value(that._dataProvider.DataSource).length;
                    if (targetIndex < 0) {
                        that._leaveAndLoadPreviousPage(delta);
                    } else if (targetIndex >= totalRowsCount) {
                        that._leaveAndLoadNextPage(delta);
                    } else {
                        that._leaveAndChangeRow(targetIndex);
                    }
                    return;
                }
                if (!isNewRow) {
                    // Let's fix the index if it is going to go outside of server cache
                    var cacheSize = $dyn.peek(that._dataProvider.CacheSize);
                    if (targetCacheIndex >= cacheSize) {
                        targetCacheIndex = cacheSize - 1;
                    }

                    if (targetCacheIndex < 0) {
                        targetCacheIndex = 0;
                    }
                }

                var targetFixedIndex = that._listContext.ConvertServerCacheIndexToPageIndex(targetCacheIndex);
                that._leaveAndChangeRow(targetFixedIndex);
            };
            this._queueExecuteAction(internalRowId, 'ChangeRowByDelta', executeAction, true);
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Integer} internalRowId - The internal row id selected by the user
        * @param {String} actionName - The action to be performed (ToggleMark, DefaultAction etc)
        * @param {Object} executeAction - The action to be performed
        * @summary If internal row id is the active row then execute immediately, else queue the action on the pending row
        */
        _queueExecuteAction: function (internalRowId, actionName, executeAction) {
            var action = {
                name: actionName,
                action: executeAction,
                cancel: function () {
                }
            };

            // When internal row id is falsy then there isn't a selected row
            if (this._canExecuteNow(internalRowId)) {
                $dyn.log.list && $dyn.log.list("List: [SyncManager] Executing actions");
                this._doExecuteActions([action]);
            } else {
                var pendingRow = this._getOrCreatePendingRow(internalRowId);
                if (pendingRow.status === PendingRowStates.notQueued) {
                    pendingRow.executeActions.push(action);
                }
                else {
                    //Pending row for this internal row Id already exists in the queue
                    //Update/Add execute actions
                    this._mergeAction(pendingRow.executeActions, action);
                }

                pendingRow.isDirty = true;
                pendingRow.processImmediate = true;

                this.queuePendingRow(pendingRow);
                this.processQueue();
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Integer} internalRowId - The internal row id selected by the user
        * @returns {Boolean} True if the internal row id is the active row and the action can be executed immediately, false otherwise
        * @summary Determines whether to execute the action immediately or not
        */
        _canExecuteNow: function (internalRowId) {
            var canExecuteNow = false;
            if (!internalRowId || this.isActiveRow(internalRowId)) {
                if (!internalRowId) {
                    canExecuteNow = true;
                }
                else {
                    var queueLength = this._pendingRowsQueue().length;
                    if (queueLength === 0 || (this._currentProcessingRow && this._currentProcessingRow.rowId === internalRowId)) {
                        canExecuteNow = true;
                    }
                }
            }

            return canExecuteNow;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Object} executeActions - An array of execute actions
        * @summary Executes the action(s)
        */
        _doExecuteActions: function (executeActions) {
            if (executeActions) {
                var listControl = this._listContext;
                for (var i = 0, len = executeActions.length; i < len; i++) {
                    executeActions[i].action.call(listControl);
                }
                executeActions.length = 0;
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Integer} internalRowId - The internal row id of row selected by the user
        * @returns {$dyn.controls.List.SyncManager.PendingRowItem} The pending row item if it already exists, else creates and returns a new pending row item
        * @summary The row item returned is then enqueued into the pending rows queue
        */
        _getOrCreatePendingRow: function (internalRowId) {
            var pendingRow = this._pendingRowIdToPendingRow[internalRowId];
            if (!pendingRow) {
                //Create pending row item and add to queue
                pendingRow = this.createPendingRow({
                    rowId: internalRowId,
                });

                this.setPendingRowType(pendingRow);
            }

            return pendingRow;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Object} rowOptions - An object which has the internal row id
        * @returns {$dyn.controls.List.SyncManager.PendingRowItem} The row item
        * @summary The row item returned is then enqueued into the pending rows queue
        */
        createPendingRow: function (rowOptions) {
            if (!this._nextNewRowId) {
                this._nextNewRowId = 1;
            } else {
                this._nextNewRowId++;
            }

            rowOptions.state = [];
            rowOptions.executeActions = [];
            rowOptions.id = this._nextNewRowId;
            rowOptions.status = PendingRowStates.notQueued;

            return rowOptions;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @public
        * @param {Integer} internalRowId - Internal row id of the row selected by the user
        * @param {String} columnName - Name of the column that has the cell changes
        * @param {Object} state - Cell state that contains the changes
        * @param {Object} commitAction - Action to be performed if sync'ing is allowed
        * @param {Object} cancelAction - Action to be performed if sync'ing is not allowed
        * @summary Called by EditManager when control state changes. Cell changes are either queued or sync'ed immediately
        */
        queueCellEdit: function (internalRowId, columnName, state, commitAction, cancelAction) {
            $dyn.log.list && $dyn.log.list("List: [SyncManager] Entering SyncManager::queueCellEdit");
            $dyn.log.list && $dyn.log.list("List: [SyncManager] queueCellEdit - InternalRowId: %s, Control name: %s,  Control state: %o", internalRowId, columnName, state);
            var pendingRow, stateItem, cellExists = false;

            if (!internalRowId) {
                return;
            }

            stateItem = { name: columnName, state: state, action: commitAction, cancel: cancelAction };

            if (this._canSyncCellChangesNow(internalRowId)) {
                $dyn.log.list && $dyn.log.list("List: [SyncManager] Syncing cell changes on active row");
                this.syncCellChanges([stateItem]);
            } else {
                var pendingRow = this._getOrCreatePendingRow(internalRowId);
                if (pendingRow.status === PendingRowStates.notQueued) {
                    pendingRow.state.push(stateItem);
                }
                else {
                    //Pending row for this internal row Id already exists in the queue
                    //Update/Add cell state
                    this._mergeAction(pendingRow.state, stateItem);
                }
                pendingRow.isDirty = true;
                pendingRow.processImmediate = true;
                this.queuePendingRow(pendingRow);
                this.processQueue();
            }

            $dyn.log.list && $dyn.log.list("List: [SyncManager] Leaving SyncManager::queueCellEdit");
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Integer} rowId - The internal row id
        * @summary Updates the pendingEdits map to indicate that pending edits are happening against this row
        */
        _addPendingEdit: function (rowId) {
            this._pendingEdits()[rowId] = true;
            this._pendingEdits.valueChanged.raise();
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Integer} rowId - The internal row id
        * @summary Deletes the row from the map to indicate no pending edits are happening on this row
        */
        _removePendingEdit: function (rowId) {
            delete this._pendingEdits()[rowId];
            this._pendingEdits.valueChanged.raise();
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Integer} internalRowId - The internal row id
        * @returns {Boolean} Flag that indicates whether cell changes can be sync'ed immediately or not
        * @summary Determines whether to synchronize the cell changes depending on the state of the pending rows queue
        */
        _canSyncCellChangesNow: function (internalRowId) {
            var canSyncCellChangesNow = false;
            if (this.isActiveRow(internalRowId)) {
                var queueLength = this._pendingRowsQueue().length;
                if (queueLength > 0 && this._currentProcessingRow && this._currentProcessingRow.rowId !== internalRowId) {
                    //Active row is changing
                    canSyncCellChangesNow = false;
                }
                else {
                    //One of three scenarios
                    //1. Queue is empty
                    //2. Queue is non-empty but no row is currently being processed (process failure scenario)
                    //3. Queue is non-empty and the current processing row is the active row
                    //In all the above cases we need to immediately sync cell changes as they are happening on the active row
                    canSyncCellChangesNow = true;
                }
            }

            return canSyncCellChangesNow;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Object} actions - A list of execute actions
        * @param {Object} action - A specific execute action
        * @summary If action exists in the action list, it updates it (in place) with the new action, else adds it to the end of list
        */
        _mergeAction: function (actions, action) {
            var actionExists = false;
            for (var i = 0, len = actions.length; i < len; i++) {
                var currentAction = actions[i];
                if (currentAction && currentAction.name === action.name) {
                    currentAction.cancel();
                    actions[i] = action;
                    actionExists = true;
                    break;
                }
            }

            if (!actionExists) {
                actions.push(action);
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Object} destinationActions - An object which has the internal row id
        * @param {Object} sourceActions - An object which has the internal row id
        * @summary For every action in sourceActions it merges it with the destinationActions list
        */
        _mergeActions: function (destinationActions, sourceActions) {
            for (var i = 0, len = sourceActions.length; i < len; i++) {
                var item = sourceActions[i];
                this._mergeAction(destinationActions, item);
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Integer} rowId - Row id of the user selected row
        * @param {Boolean} isSyncing - flag that determines whether the row is currently in the pending rows queue
        * @summary Sets the isSyncing value against the internal row id in the map
        */
        _setRowSyncState: function (rowId, isSyncing) {
            if (rowId) {
                var syncingRows = this.rowSyncState();

                // truthy compare
                if (syncingRows[rowId] != isSyncing) {
                    if (!isSyncing) {
                        delete syncingRows[rowId];
                    } else {
                        syncingRows[rowId] = true;
                    }

                    this.rowSyncState.valueChanged.raise();
                }
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Object} pendingRow - The pending row item
        * @summary Queues a row item into the pending rows queue.
        * @description If the row item already exists then execute actions and cell changes get merges.
        *              If the row item in front of it is not currently being processed and is not dirty, then it gets removed from the queue
        */
        queuePendingRow: function (pendingRow) {
            $dyn.log.list && $dyn.log.list("List: [SyncManager] Entering SyncManager::queuePendingRow");
            $dyn.log.list && $dyn.log.list("List: [SyncManager] Request to queue row id %s %o", pendingRow.rowId, pendingRow);
            var existingPendingRow = this._pendingRowIdToPendingRow[pendingRow.rowId];
            if (!existingPendingRow) {
                $dyn.log.list && $dyn.log.list("List: [SyncManager] Pending row doesn't exist in the queue");
                if (pendingRow.rowId) {
                    this._pendingRowIdToPendingRow[pendingRow.rowId] = pendingRow;
                }
                pendingRow.status = PendingRowStates.pending;

                $dyn.ui.updateBlock(null, function () {
                    this.runDiscardPolicy();

                    $dyn.log.list && $dyn.log.list("List: [SyncManager] Queueing pending row");
                    this._pendingRowsQueue.push(pendingRow);
                }, this);

            } else if (existingPendingRow !== pendingRow) {
                $dyn.log.list && $dyn.log.list("List: [SyncManager] Row exists in queue with same row id, but it's not the pendingRow");
                existingPendingRow.processImmediate = existingPendingRow.processImmediate || pendingRow.processImmediate;
                existingPendingRow.isDirty = existingPendingRow.isDirty || pendingRow.isDirty;
                this._mergeActions(existingPendingRow.state, pendingRow.state);
                this._mergeActions(existingPendingRow.executeActions, pendingRow.executeActions);
                if (this._dataProvider.deleteTriggered && this._dataProvider._firstRowDeleted) {
                    existingPendingRow.isFromKeyDown = pendingRow.isFromKeyDown;
                }
                pendingRow = existingPendingRow;
            }

            if (pendingRow.isDirty) {
                this._setRowSyncState(pendingRow.rowId, true);
            }

            if (pendingRow.processImmediate) {
                var queue = this._pendingRowsQueue();
                if (queue) {
                    queue[0].processImmediate = true;
                }
            }
            // The pending row in the queue is considered to be a pending edit iof it is a new row or cell edit change
            if ((pendingRow.state && pendingRow.state.length) || pendingRow.type === PendingRowTypes.newRow || pendingRow.isFromKeyDown) {
                this._addPendingEdit(pendingRow.rowId);
            }
            $dyn.log.list && $dyn.log.list("List: [SyncManager] Leaving SyncManager::queuePendingRow");
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @summary Determines if the tail of the pending rows queue can be removed. If it is not currently being processed and is not dirty, then it gets removed from the queue
        */
        runDiscardPolicy: function () {
            var queue = this._pendingRowsQueue();
            var tail = queue[queue.length - 1];
            if (tail && tail !== this._currentProcessingRow && !tail.isDirty) {
                this.dequeuePendingRow(tail);
                this._setRowSyncState(tail.rowId, false);
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Object} pendingRow - The pending row item
        * @summary Remove row item from the pending rows queue
        */
        dequeuePendingRow: function (pendingRow) {
            this._currentProcessingRow = null;
            delete this._pendingRowIdToPendingRow[pendingRow.rowId];
            var index = this._pendingRowsQueue().indexOf(pendingRow);
            if (index !== -1) {
                $dyn.log.list && $dyn.log.list("List: [SyncManager] Removing Internal RowId %s from queue", pendingRow.rowId);
                $dyn.ui.updateBlock(null, function () {
                    this._pendingRowsQueue.splice(index, 1);
                    this._setRowSyncState(pendingRow.rowId, false);
                    this._removePendingEdit(pendingRow.rowId);
                }, this);
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Integer} internalRowId - Row id of the internal row
        * @summary Remove row item from the pending rows to direct interaction queue
        */
        dequeuePendingRowsToDirectInteractionQueue: function (internalRowId) {
            var rowIndex = this._listContext._renderer.getActivePageRelatedIndex(internalRowId);
            $dyn.log.list && $dyn.log.list("List: [SyncManager] Remove row index %s from Direct Interaction Queue", rowIndex);
            for (var i = 0; i < $dyn.peek(this._pendingRowsToDirectInteractionQueue).length; ++i) {
                var pendingRow = $dyn.peek(this._pendingRowsToDirectInteractionQueue)[i];
                if (pendingRow && pendingRow.rowId === internalRowId) {
                    this._pendingRowsToDirectInteractionQueue.splice(i, 1);
                }
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @returns {Integer} Sync Engine state (Active or Inactive)
        * @summary Sync Engine is active if either of the queues are non-empty AND either the active row is valid or sync is currently in progress
        */
        _getSyncEngineState: function () {
            var syncEngineState;
            if (($dyn.length(this._pendingRowsQueue) > 0 || $dyn.length(this._pendingRowsToDirectInteractionQueue) > 0) && (this._activeRowValid() || this._syncInProgress())) {
                $dyn.log.list && $dyn.log.list("List: [SyncManager] SyncEngine active");
                syncEngineState = $dyn.util.SyncEngineState.active;
            }
            else {
                $dyn.log.list && $dyn.log.list("List: [SyncManager] SyncEngine inactive");
                syncEngineState = $dyn.util.SyncEngineState.inactive;
            }
            return syncEngineState;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @summary Processes the next row item in the queue.
        * @description If the row is the active row and is not dirty then it is removed from the queue
        *              If the row is the active row and is dirty then execute actions and/or cell changes get sync'ed
        * @param {Boolean} isNewRow - Flag indicating if this is called for a new row created in the flow.
        * @param {Boolean} isRowKeyDownNewRowInNonFastEditMode - Flag indicating if this is called from Row Key Down flow of a non fast edit grid.
        */
        processQueue: function (isNewRow, isRowKeyDownNewRowInNonFastEditMode) {
            $dyn.log.list && $dyn.log.list("List: [SyncManager] Entering SyncManager::processQueue");
            var rows = this._pendingRowsQueue();
            var queueLength = rows && rows.length;
            $dyn.log.list && $dyn.log.list('List: [SyncManager] Processing queue: length: %o, row processing: %o, active row valid: %s, should reprocess: %s, queue: %o',
                queueLength,
                this._currentProcessingRow,
                this._activeRowValid(),
                this._shouldReprocess(),
                rows.slice(0));

            if (this.isPendingRowsToDirectInteractionQueueEmpty() && !this.isQueueEmpty() && !this._currentProcessingRow && (this._activeRowValid() || this._shouldReprocess())) {
                var pendingRow = rows[0];
                var internalRowId = pendingRow.rowId;
                if (!pendingRow.isDirty && this.isActiveRow(internalRowId) /*&& !this._dataProvider.hasMarkedRows(true)*/) {
                    $dyn.log.list && $dyn.log.list("List: [SyncManager] Attempting to process active row that's not dirty. Abort processing");
                    this.dequeuePendingRow(pendingRow);
                    this.processQueue();
                    return;
                }
                else {
                    if (pendingRow.isDirty && this.isActiveRow(internalRowId)) {
                        //Execute any pending actions on this pending row
                        //Remove row item from queue
                        $dyn.log.list && $dyn.log.list("List: [SyncManager] Calling Execute Actions for internalRowId %s", internalRowId);
                        this._doExecuteActions(pendingRow.executeActions);

                        $dyn.log.list && $dyn.log.list("List: [SyncManager] Syncing cell changes for internalRowId %s", internalRowId);
                        this.syncCellChanges(pendingRow.state);

                        this.dequeuePendingRow(pendingRow);
                        this.processQueue();
                        return;
                    }
                    else {
                        var self = this;

                        if (pendingRow.type === PendingRowTypes.existingRow) {
                            var rowIndex = this._listContext._renderer.getActivePageRelatedIndex(internalRowId);
                            if (rowIndex !== null && rowIndex !== undefined) {
                                this._setRowStateToProcessing(pendingRow);
                                this._leaveAndChangeRow(rowIndex, pendingRow.rowId, true);
                            }
                            else {
                                $dyn.log.list && $dyn.log.list("List: [SyncManager] Attempting to change to an invalid row index: %s. Abort processing", rowIndex);
                                this.dequeuePendingRow(pendingRow);
                                this.processQueue();
                                return;
                            }
                        }
                        else {
                            //New row
                            if (pendingRow.isFromKeyDown) {
                                this._setRowStateToProcessing(pendingRow);
                                this._leaveAndCreateRowOnKeyDown(pendingRow);
                            }
                            else {
                                //Alt+N
                                var relativeDataRowId = this._dataProvider.getRelativeDataRowIdByInternalRowId(internalRowId);
                                if (relativeDataRowId) {
                                    var relativeRowIndex = this._listContext._renderer.getActivePageRelatedIndex(this._dataProvider.getInternalRowId(relativeDataRowId));
                                    if ((relativeRowIndex !== null && relativeRowIndex !== undefined) || relativeDataRowId == -1) {
                                        if (relativeDataRowId == -1) {
                                            relativeRowIndex = null;
                                        }
                                        this._setRowStateToProcessing(pendingRow);
                                        this._leaveAndCreateRow(relativeRowIndex, internalRowId);
                                    }
                                    else {
                                        $dyn.log.list && $dyn.log.list("List: [SyncManager] Unable to locate a reference row index to create the new row. Abort processing");
                                        this.dequeuePendingRow(pendingRow);
                                        this.processQueue();
                                        return;
                                    }
                                } else {
                                    $dyn.log.list && $dyn.log.list("List: [SyncManager] Unable to locate a reference internal row id to create the new row");
                                    this.dequeuePendingRow(pendingRow);
                                    this.processQueue();
                                    return;
                                }
                            }
                        }
                    }
                }
            }
            else if (this.isPendingRowsToDirectInteractionQueueEmpty() && this.isQueueEmpty()) {
                this._syncInProgress(false);
                this.syncRowAndCell(isNewRow, isRowKeyDownNewRowInNonFastEditMode);
            }

            $dyn.log.list && $dyn.log.list("List: [SyncManager] Leaving SyncManager::processQueue");
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {$dyn.controls.List.SyncManager.PendingRowItem} pendingRow - Pending row item
        * @summary Sets the row sync state on the pending row
        */
        _setRowStateToProcessing: function (pendingRow) {
            $dyn.log.list && $dyn.log.list("List: [SyncManager] Setting row state to processing");
            pendingRow.status = PendingRowStates.processing;
            this._setRowSyncState(pendingRow.rowId, true);
            this._currentProcessingRow = pendingRow;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Integer} delta - A relative position to change row to from current row
        * @summary Loads previous page
        */
        _loadPreviousPage: function (delta) {
            var listContext = this._listContext;
            var that = this;
            var previousPageCallback = function previousPageCallback(success) {
                if (!success) {
                    $dyn.async(function () {
                        // If LoadNextPage was not successful, go to the top row in the current page.
                        that._dataProvider.changeRow(0);
                    }, undefined, "Go to top row in current page");
                } else {
                    that.callbackSuccess();
                }
            };
            this._syncInProgress(true);
            $dyn.callFunction(listContext.LoadPreviousPage, listContext, [Math.abs(delta).toString()], previousPageCallback);
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Integer} delta - A relative position to change row to from current row
        * @summary Loads next page
        */
        _loadNextPage: function (delta) {
            var listContext = this._listContext;
            var that = this;
            var totalRowsCount = $dyn.value(this._dataProvider.DataSource).length;
            // Bring in next page of records and set the next active row to the corresponding index in the next page.
            var nextPageCallback = function nextPageCallback(success) {
                if (!success) {
                    $dyn.async(function () {
                        // If LoadNextPage was not successful, go to the bottom row in the current page.
                        that._dataProvider.changeRow(totalRowsCount - 1);
                    }, undefined, "Go to bottom row in the current page.");
                } else {
                    that.callbackSuccess();
                }
            };
            this._syncInProgress(true);
            $dyn.callFunction(listContext.LoadNextPage, listContext, [delta.toString()], nextPageCallback);
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Integer} relativeRowIndex - A relative position to change row to from current row
        * @param {Integer} internalRowId - Row id of newly created row
        * @summary Leaves current record and creates new row relative to the specified row index
        */
        _leaveAndCreateRow: function (relativeRowIndex, internalRowId) {
            $dyn.log.list && $dyn.log.list("List: [SyncManager] Entering SyncManager::_leaveAndCreateRow");
            var self = this;

            if (this._listContext._fastEditingAllowed()) {
                $dyn.microTask(function () {
                    self._leaveRecord(true).done(function () {
                        self.createRow(relativeRowIndex, internalRowId);
                    });
                }, self);
            } else {
                this.createRow(relativeRowIndex, internalRowId);
            }

            $dyn.log.list && $dyn.log.list("List: [SyncManager] Leaving SyncManager::_leaveAndCreateRow");
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {$dyn.controls.List.SyncManager.PendingRowItem} pendingRow - Pending row item
        * @summary Leaves current active row and creates new row from the last row in server db cache
        */
        _leaveAndCreateRowOnKeyDown: function (pendingRow) {
            $dyn.log.list && $dyn.log.list("List: [SyncManager] Entering SyncManager::_leaveAndCreateRowOnKeyDown");
            var self = this;
            var markMode, rangePivotWithinPage;
            markMode = this._listContext._markingAction;
            rangePivotWithinPage = this._listContext.rangePivotWithinPage();

            if (this._listContext._fastEditingAllowed()) {
                $dyn.microTask(function () {
                    self._leaveRecord(true).done(function () {
                        self.createRowOnKeyDown(pendingRow, markMode, rangePivotWithinPage);
                    });
                }, self);
            } else {
                this.createRowOnKeyDown(pendingRow, markMode, rangePivotWithinPage);
            }

            $dyn.log.list && $dyn.log.list("List: [SyncManager] Leaving SyncManager::_leaveAndCreateRowOnkeyDown");
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Integer} rowIndex - row index to change row to
        * @param {Integer} internalRowId - row id of user selected row
        * @param {Boolean} isFromQueue - Flag that determines if the change row request is from row item in pending rows queue
        * @summary Leaves current active row and changes row to specified row index
        */
        _leaveAndChangeRow: function (rowIndex, internalRowId, isFromQueue) {
            $dyn.log.list && $dyn.log.list("List: [SyncManager] Entering SyncManager::_leaveAndChangeRow");
            var self = this;

            if (this._listContext._fastEditingAllowed()) {
                $dyn.microTask(function () {
                    self._leaveRecord(true).done(function () {
                        self.changeRow(rowIndex, internalRowId, isFromQueue);
                    });
                }, self);
            } else {
                this.changeRow(rowIndex, internalRowId, isFromQueue);
            }

            $dyn.log.list && $dyn.log.list("List: [SyncManager] Leaving SyncManager::_leaveAndChangeRow");
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Integer} delta - A relative position to change row to from current row
        * @summary Leaves current active row and loads next page
        */
        _leaveAndLoadNextPage: function (delta) {
            var that = this;
            if (this._listContext._fastEditingAllowed()) {
                $dyn.microTask(function () {
                    that._leaveRecord(true).done(function () {
                        that._loadNextPage(delta);
                    });
                }, that);
            } else {
                that._loadNextPage(delta);
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Integer} delta - A relative position to change row to from current row
        * @summary Leaves current active row and loads previous page
        */
        _leaveAndLoadPreviousPage: function (delta) {
            var that = this;
            if (this._listContext._fastEditingAllowed()) {
                $dyn.microTask(function () {
                    that._leaveRecord(true).done(function () {
                        that._loadPreviousPage(delta);
                    });
                }, that);
            } else {
                this._loadPreviousPage(delta);
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Boolean} isFromPendingRowsQueue - Flag that determines if the leave record is being processed for a row in the pending rows queue
        * @summary Leaves current active row by changing row to the current selected index
        * @returns {object} - The promise that will be resolved in the callback
        */
        _leaveRecord: function (isFromPendingRowsQueue) {
            // 'Leave record' simulation by selecting record with already selected index
            var that = this;
            var result = new $.Deferred();
            var currentSelectedIndex = $dyn.value(this._dataProvider.SelectedIndex);
            $dyn.log.list && $dyn.log.list("List: [SyncManager] Entering SyncManager::LeaveRecord Active Row Index:%s, IsFromPendingRowsQueue:%s", currentSelectedIndex, isFromPendingRowsQueue);
            // Call 'leaveRecord' only if current record is within active page
            // Otherwise it would cause server (yes we are making some assumptions:)) to rebuild page around this row
            // That is undesired side effect
            if (this._dataProvider.isIndexWithinPage(currentSelectedIndex)) {
                var dataIdBeforeLeave = this._dataProvider.getDataRowIdByPageIndex(currentSelectedIndex);
                this._listContext._initialDataStartPosition = $dyn.value(this._dataProvider.FetchedDataStartPosition);

                var callback = function (callbackResult) {
                    that._leaveRecordCallback(callbackResult, dataIdBeforeLeave, currentSelectedIndex);
                    if (callbackResult) {
                        result.resolve();
                    }
                    else {
                        that._leaveRecordRowIndex = null;
                    }
                };

                this.changeRow(currentSelectedIndex, null, isFromPendingRowsQueue, true, callback);
            } else {
                result.resolve();
            }

            $dyn.log.list && $dyn.log.list("List: [SyncManager] Leaving SyncManager::_leaveRecord");
            return result.promise();
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Boolean} result - Result of the leaveRecord call.
        * @param {Integer} oldDataId - Master tracking id of the row before the row change
        * @param {Integer} rowIndex - row index to change row to
        * @summary Upon a successful leave record, if the master tracking if on the row has changed, then the internal data provider maps get updated
        */
        _leaveRecordCallback: function (result, oldDataId, rowIndex) {
            $dyn.log.list && $dyn.log.list("List: [SyncManager] Entering SyncManager::_leaveRecordCallback");
            var internalRowId = this._dataProvider.getInternalRowId(oldDataId);
            if (result) {
                $dyn.log.list && $dyn.log.list("List: [SyncManager] Leave Record for row id %s, succeeded", internalRowId);
                var selectedIndex = $dyn.value(this._dataProvider.SelectedIndex);
                var newDataId = this._dataProvider.getDataRowIdByPageIndex(selectedIndex);
                if (newDataId !== oldDataId && rowIndex === selectedIndex) {
                    this._dataProvider.updateDataId(oldDataId, newDataId);
                    $dyn.log.list && $dyn.log.list("List: [SyncManager] LeaveRecord changed data id - data id before: " + oldDataId + " After: " + newDataId);
                }
            } else {
                $dyn.log.list && $dyn.log.list("List: [SyncManager] Leave Record for row id %s, failed", internalRowId);
                this.callbackFailure(internalRowId, false);
            }

            $dyn.log.list && $dyn.log.list("List: [SyncManager] Leaving SyncManager::_leaveRecordCallback");
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Integer} relativeRowIndex - Row index relative to which the new row will be created
        * @param {Integer} internalRowId - Row id of the new row being created
        * @summary Calls the CreateNewRecord server grid API to create a new row on the server
        */
        createRow: function (relativeRowIndex, internalRowId) {
            var self = this;
            $dyn.microTask(function () {
                $dyn.log.list && $dyn.log.list("List: [SyncManager] Sending New Row interaction to server (Alt+N)");
                //Set the internalTrackingId on the new model being created on the server
                this._syncInProgress(true);
                $dyn.callFunction(self._listContext.CreateNewRecord, self._listContext, [relativeRowIndex], function (createdRowId) {
                    self.createNewRecordCallback(createdRowId != -1, internalRowId);
                });
            }, self);
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {$dyn.controls.List.SyncManager.PendingRowItem} pendingRow - A pending row item
        * @param {Integer} markMode - The mark mode
        * @param {Integer} rangePivotWithinPage - Number of rows to mark relative to current active row
        * @summary Calls the CreateNewRecordOnKeyDown server grid API to create a new row
        */
        createRowOnKeyDown: function (pendingRow, markMode, rangePivotWithinPage) {
            var self = this;
            var internalRowId = pendingRow.rowId;

            $dyn.microTask(function () {
                $dyn.log.list && $dyn.log.list("List: [SyncManager] Sending New Row interaction to server (Key Down)");
                //Set the internalTrackingId on the new model being created on the server
                var isFastEdit = $dyn.peek(self._listContext._fastEdit);
                this._syncInProgress(true);
                $dyn.callFunction(self._listContext.CreateNewRecordOnKeyDown, self._listContext, [markMode, rangePivotWithinPage, internalRowId, isFastEdit], function (createdRowId) {
                    self.createNewRecordCallback(createdRowId != -1, internalRowId);
                });
            }, self);
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Integer} rowIndex - Row index to change row to
        * @param {Integer} internalRowId - Row id of user selected row
        * @param {Boolean} isFromQueue - Flag that determines if the change row is being processed for a row in the pending rows queue
        * @param {Boolean} isFromLeaveRecord - Flag that determines if the change row is due to a leave record
        * @param {Object} callback - Callback function that will be executed upon a successful row change
        * @summary Calls the ActiveRowChanged server grid API that changes active row on server
        */
        changeRow: function (rowIndex, internalRowId, isFromQueue, isFromLeaveRecord, callback) {
            $dyn.log.list && $dyn.log.list("List: [SyncManager] Sending Change Row Interaction to row index: %s", rowIndex);

            var self = this;
            var inputMode = this._listContext.InputMode;
            var shouldThrottle = !isFromQueue && (inputMode === $dyn.ui.InputType.keyboard);

            var markMode, rangePivotWithinPage;

            if (!callback) {
                callback = function (result) {
                    self.changeSelectedIndexCallback(result, internalRowId, isFromQueue);
                };
            }

            if (isFromLeaveRecord) {
                markMode = $dyn.controls.List.MarkMode.none;
                rangePivotWithinPage = null;

                this._syncInProgress(true);
                var beforeExecute = function () {
                    self._pendingRowChange(true);
                };
                $dyn.callFunction(
                    this._listContext.ChangeSelectedIndex,
                    this._listContext,
                    [rowIndex, markMode, rangePivotWithinPage, shouldThrottle, callback, undefined /*completeCallback*/, true /*skip recording*/, beforeExecute]);
            }
            else {
                markMode = this._listContext._markingAction;
                rangePivotWithinPage = this._listContext.rangePivotWithinPage();

                var callChangeSelectedIndex = function () {
                    // Needs to be a microTask so SetValue and ChangeSelectedIndex are sent to server together in one request
                    $dyn.microTask(function () {
                        $dyn.log.list && $dyn.log.list("List: [SyncManager] Executing changeRow micro task for row index %s", rowIndex);
                        this._listContext._initialDataStartPosition = $dyn.value(this._dataProvider.FetchedDataStartPosition);
                        this._syncInProgress(true);
                        if (!$dyn.value(this._pendingRowChange) || $dyn.value(this._listContext._fastEdit)) {
                            // Don't change callstack for fastEdit
                            var beforeExecute = function () {
                                self._pendingRowChange(true);
                            };
                            $dyn.callFunction(
                                this._listContext.ChangeSelectedIndex,
                                this._listContext,
                                [rowIndex, markMode, rangePivotWithinPage, shouldThrottle, callback, undefined /*completeCallback*/, false /*skip recording*/, beforeExecute]);
                        } else {
                            if (this._pendingRowChangeSubscriber !== undefined && this._pendingRowChangeSubscriber !== null) {
                                this.dequeuePendingRowsToDirectInteractionQueue(this._pendingRowChangeId);
                                this._pendingRowChange.valueChanged.unsubscribe(this._pendingRowChangeSubscriber);
                                this._pendingRowChangeSubscriber = null;
                            }
                            // If there is currently a CSI that is sent to the server but has not come back, wait for that to complete before sending another one
                            this._pendingRowChangeSubscriber = this._pendingRowChange.valueChanged.subscribe(function (newValue) {
                                if (!$dyn.value(newValue)) {
                                    self._pendingRowChange.valueChanged.unsubscribe(self._pendingRowChangeSubscriber);
                                    var beforeExecute = function () {
                                        self._pendingRowChange(true);
                                    };
                                    $dyn.callFunction(
                                        self._listContext.ChangeSelectedIndex,
                                        self._listContext,
                                        [rowIndex, markMode, rangePivotWithinPage, shouldThrottle, callback, undefined /*completeCallback*/, false /*skip recording*/, beforeExecute]);
                                }
                            });

                            this._pendingRowChangeId = internalRowId;
                        }
                    }, self);
                };

                if ($dyn.value(this._listContext._dataLoadPending) > 0 || $dyn.value(this._listContext._dataLoadedRenderPending)) {
                    $dyn.observe(this._listContext._dataLoadPending, function (dataLoadPendingCount) {
                        if ($dyn.value(dataLoadPendingCount) === 0) {
                            $dyn.observe(self._listContext._dataLoadedRenderPending, function (isRenderPending) {
                                if (!$dyn.value(isRenderPending)) {
                                    // need to recalculate the row index after the data load is completed
                                    rowIndex = self._listContext._renderer.getActivePageRelatedIndex(internalRowId);
                                    callChangeSelectedIndex();
                                    return false;
                                }
                            });
                            return false;
                        }
                    });
                } else {
                    callChangeSelectedIndex();
                }
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Boolean} result - Flag that indicates the result of the change row
        * @param {Integer} internalRowId - Row id of the user selected row
        * @param {Boolean} isFromQueue - Flag that indicates if the change row is being processed for a row in the pending rows queue
        * @summary Executes the callback of a change row interaction
        */
        changeSelectedIndexCallback: function (result, internalRowId, isFromQueue) {
            $dyn.log.list && $dyn.log.list("List: [SyncManager] Entering SyncManager::changeSelectedIndexCallback for row id %s:", internalRowId);
            //Part of callback processing involves syncing cell changes which could result in new command interactions
            //being sent out. This cannot happen in the same callback loop of the previous interaction.
            //Hence we need to process callbacks in a micro-task
            $dyn.microTask(function () {
                if (result === 1 || result === true) {
                    this._clientInitiatedRowChange = true;
                    this.callbackSuccess(internalRowId, isFromQueue);
                }
                else if (result === 0 || result === false) {
                    this.callbackFailure(internalRowId, isFromQueue);
                    // If CSI failed, do not send any pending CSI as they will probably fail as well
                    if (this._pendingRowChangeSubscriber !== undefined && this._pendingRowChangSubscriber !== null) {
                        this._pendingRowChange.valueChanged.unsubscribe(this._pendingRowChangeSubscriber);
                        this._pendingRowChangeSubscriber = null;
                    }
                }
                else {
                    $dyn.log.list && $dyn.log.list("List: [SyncManager] Interaction for row id %s, throttled", internalRowId);
                }
                this._pendingRowChange(false);

                if (!isFromQueue) {
                    this.dequeuePendingRowsToDirectInteractionQueue(internalRowId);
                }
            }, this);
            $dyn.log.list && $dyn.log.list("List: [SyncManager] Leaving SyncManager::changeSelectedIndexCallback");
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Boolean} success - Flag that determines if the new record interaction was successful or not
        * @param {Integer} internalRowId - Row id of the user selected row
        * @summary Callback for the create new record
        */
        createNewRecordCallback: function (success, internalRowId) {
            $dyn.log.list && $dyn.log.list("List: [SyncManager] Entering SyncManager::createNewRecordCallback");

            if ($dyn.peek(this._listContext._fastEdit)) {
                var selectedIndex = this._listContext.SelectedIndex();
                //This will also update the data selected row id to the internal selected row id
                this._dataProvider.updatePivotTables(internalRowId, selectedIndex);
            }
            //Part of callback processing involves syncing cell changes which could result in new command interactions
            //being sent out. This cannot happen in the same callback loop of the previous interaction.
            //Hence we need to process callbacks in a micro-task
            $dyn.microTask(function () {
                if (success) {
                    this.callbackSuccess(internalRowId, true);
                }
                else {
                    this.callbackFailure(internalRowId, true);
                }
            }, this);
            $dyn.log.list && $dyn.log.list("List: [SyncManager] Leaving SyncManager::createNewRecordCallback");
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Integer} internalRowId - Row id of the user selected row
        * @param {Boolean} isFromQueue - Flag that determines if the row being processed is from the pending rows queue
        * @summary Callback function for a successful change/create row interaction
        */
        callbackSuccess: function (internalRowId, isFromQueue) {
            var self = this;
            $dyn.log.list && $dyn.log.list("List: [SyncManager] Callback success for internalRowId %s", internalRowId);

            //Check pending queue to sync any changes for this row
            if (isFromQueue) {
                var dataSelectedRowId;
                var pendingRow = this._currentProcessingRow;
                if (pendingRow && pendingRow.status === PendingRowStates.processing) {
                    this._doExecuteActions(pendingRow.executeActions);
                    $dyn.log.list && $dyn.log.list("List: [SyncManager] Syncing cell changes for internalRowId %s", internalRowId);
                    this.syncCellChanges(pendingRow.state);

                    this._currentProcessingRow = null;
                    var isNewRow, isRowKeyDownNewRowInNonFastEditMode;
                    isNewRow = (pendingRow.type !== PendingRowTypes.existingRow);
                    isRowKeyDownNewRowInNonFastEditMode = pendingRow.rowKeyDownNewRowInNonFastEditMode;
                    this.dequeuePendingRow(pendingRow);
                    this.processQueue(isNewRow, isRowKeyDownNewRowInNonFastEditMode);
                }
            }
            else {
                this._syncInProgress(false);
            }

            $dyn.ui.updateBlock(null, function () {
                this.setInvalidRow(null);
                this.setShouldReprocess(false);
            }, this);
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Boolean} isNewRow - Flag that determines if it is a new row
        * @param {Boolean} isRowKeyDownNewRowInNonFastEditMode - Flag that determines if this is a new row scenario on row key down from non-fast edit grid
        * @summary Synchronizes both the current active row and the current active control.  This method is called when both grid queues are empty.
        */
        syncRowAndCell: function (isNewRow, isRowKeyDownNewRowInNonFastEditMode) {
            var internalSelectedRowId = this._dataProvider.InternalSelectedRowId();
            var dataSelectedRowId = this._dataProvider.DataSelectedRowId();
            var rowIndex = this._listContext._renderer && this._listContext._renderer.getActivePageRelatedIndex(internalSelectedRowId);
            if (internalSelectedRowId != dataSelectedRowId && !isRowKeyDownNewRowInNonFastEditMode && rowIndex !== undefined) {
                $dyn.log.list && $dyn.log.list("List: [SyncManager] Internal selected row id: %s different from data selected row id: %s", internalSelectedRowId, dataSelectedRowId);
                this._leaveAndChangeRow(rowIndex, internalSelectedRowId);
            }

            var currentActiveControl = this._listContext.Root && this._listContext.Root.ActiveControl();
            var activeCell = $dyn.value(this._listContext.GetActiveCell());
            $dyn.log.list && $dyn.log.list("List: [SyncManager] Current Active Control: %s;  User active control %s", currentActiveControl, activeCell);
            if (activeCell && !isNewRow && rowIndex !== undefined) {
                $dyn.microTask(function () {
                    $dyn.callFunction(this._listContext.SelectCell, this._listContext, [rowIndex, activeCell, $dyn.ui.InputType.notSpecified, false]);
                }, this);
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Boolean} internalRowId - Row id of user selected row
        * @param {Boolean} isFromQueue - Flag that determines if the row being processed is from the pending rows queue
        * @param {Boolean} isFromLeaveRecord - Flag that determines if the callback was from a leave record
        * @summary Callback function when change row or create row fail
        */
        callbackFailure: function (internalRowId, isFromQueue, isFromLeaveRecord) {
            $dyn.log.list && $dyn.log.list("List: [SyncManager] Callback failed for internal row id %s", internalRowId);
            //Change Row callback failed
            var pendingRow = this._currentProcessingRow;

            if (pendingRow) {
                pendingRow.status = PendingRowStates.pending;
            }

            this._currentProcessingRow = null;

            //Keep the active row valid for scenarios where we are keying down the last row in a lookup
            if (pendingRow && pendingRow.rowKeyDownNewRowInNonFastEditMode && !pendingRow.isDirty && this._activeRowValid()) {
                $dyn.ui.updateBlock(null, function () {
                    this.setShouldReprocess(false);
                    this._syncInProgress(false);
                }, this);
            }
            else {
                $dyn.ui.updateBlock(null, function () {
                    //No need to set invalid row if active row is already false
                    //Note:This can happen when a user enters an invalid value in an active row and keys down at least 2 rows, fast
                    //In that case there will be at least two change row interactions that will fail as both directly enter into the interaction queue
                    //The active row will be set to false after the first change row failure
                    if (this._activeRowValid()) {
                        this.setInvalidRow(internalRowId);
                    }
                    this.setShouldReprocess(false);
                    this._syncInProgress(false);
                }, this);
            }

            if (pendingRow && !pendingRow.isDirty && (isFromQueue || isFromLeaveRecord)) {
                this.dequeuePendingRow(pendingRow);
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Object} state - The cell state that includes pending cell edits
        * @summary Synchronizes cell changes to server
        * @returns {object} The deferred object
        */
        syncCellChanges: function (state) {
            $dyn.log.list && $dyn.log.list("List: [SyncManager] Entering SyncManager::syncCellChanges");
            var self = this;
            var result;
            if (state && state.length > 0) {
                $dyn.log.list && $dyn.log.list("List: [SyncManager] State to sync: %o", state);

                var promises = $.map(state, function (stateItem) {
                    return stateItem.action();
                });

                result = $.when.apply($.when, promises).done(function () {
                    $dyn.log.list && $dyn.log.list("List: [SyncManager] All cell changes have been successfully synced");
                    $dyn.ui.updateBlock(null, function () {
                        self.setInvalidRow(null);
                        self._syncInProgress(false);
                    }, this);

                }).fail(function () {
                    $dyn.log.list && $dyn.log.list("List: [SyncManager] One or more cell changes have failed to sync");
                    $dyn.ui.updateBlock(null, function () {
                        self.setInvalidRow($dyn.peek(self._dataProvider.DataSelectedRowId));
                        self._syncInProgress(false);
                    }, this);
                });

                state.length = 0;
            }
            else {
                $dyn.log.list && $dyn.log.list("List: [SyncManager] No state to sync");
                result = new $.Deferred();
                result.resolve();
            }

            $dyn.log.list && $dyn.log.list("List: [SyncManager] Leaving SyncManager::syncCellChanges");
            return result;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Integer} rowId - Row id of user selected row
        * @summary Sets the row id as the invalid row. This method gets called upon a failure to change/create row
        */
        setInvalidRow: function (rowId) {
            this.invalidRow(rowId);
            $dyn.log.list && $dyn.log.list("List: [SyncManager] Invalid row id : %s", rowId);
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @private
        * @param {Boolean} value - Flag that determines whether the shouldReprocess flag is set to true or false
        * @summary If value is true then shouldReprocess flag is set to true.
        */
        setShouldReprocess: function (value) {
            this._shouldReprocess(value);
            $dyn.log.list && $dyn.log.list("List: [SyncManager] ShouldReprocess flag is : %s", this._shouldReprocess());
        },

        /**
        * @function
        * @memberof $dyn.controls.List.SyncManager#
        * @public
        * @summary Disposes all observers and handlers
        */
        dispose: function () {
            for (var i = 0; i < this._disposables.length; ++i) {
                var disposable = this._disposables[i];
                if (disposable && disposable.dispose && typeof(disposable.dispose) === "function") {
                    disposable.dispose();
                }
            }
        },
    };

})();